import { resolveXrKeyboardSticks, toggleXrKeyboardCruise, XR_KEYBOARD_MOVEMENT } from './_pure/xrKeyboard.js';
const editable = (target) => {
    const element = target;
    return !!element && (element.isContentEditable === true || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName ?? ''));
};
/**
 * Physical keyboard shortcuts for XR sessions: tapped movement keys latch cruising in place of the controller sticks and
 * single-owner commands mirror controller bindings. Owners that capture typing must stop key events before they reach
 * this registry.
 */
export function createXrKeyboard() {
    // Physically pressed movement keys (to tell repeats apart) and the movement they latched.
    const pressed = new Set();
    let cruise = new Set();
    const bindings = new Map();
    const listeners = new Set();
    let snapshot = [];
    const emptyStats = () => ({ downs: 0, repeats: 0, ups: 0, shortestRegrabMs: Infinity,
        longestHeldSilenceMs: 0, lastEventMs: NaN, releases: 0 });
    let stats = emptyStats();
    let trace = [];
    const lastSeen = new Map();
    const releasedAt = new Map();
    const record = (code, kind, time) => {
        if (kind !== 'repeat' && trace.length < 400)
            trace.push([kind === 'down' ? 'd' : 'u', code, Math.round(time ?? NaN)]);
        if (time === undefined)
            return;
        if (kind === 'down')
            stats.downs++;
        else if (kind === 'repeat')
            stats.repeats++;
        else
            stats.ups++;
        const seen = lastSeen.get(code);
        if (kind !== 'down' && seen !== undefined)
            stats.longestHeldSilenceMs = Math.max(stats.longestHeldSilenceMs, time - seen);
        const released = releasedAt.get(code);
        if (kind === 'down' && released !== undefined)
            stats.shortestRegrabMs = Math.min(stats.shortestRegrabMs, time - released);
        if (kind === 'up') {
            lastSeen.delete(code);
            releasedAt.set(code, time);
        }
        else
            lastSeen.set(code, time);
        stats.lastEventMs = time;
    };
    const publish = () => {
        snapshot = [...bindings.values()];
        listeners.forEach(listener => listener());
    };
    return {
        bind(code, binding) {
            const existing = bindings.get(code);
            if (existing)
                throw new Error(`XR key ${binding.key} is already bound by ${existing.owner}; ${binding.owner} cannot claim it.`);
            if (code in XR_KEYBOARD_MOVEMENT)
                throw new Error(`XR key ${binding.key} is reserved for movement; ${binding.owner} cannot claim it.`);
            bindings.set(code, binding);
            publish();
            return () => {
                if (bindings.get(code) !== binding)
                    return;
                bindings.delete(code);
                publish();
            };
        },
        /** Returns whether the key was consumed. */
        keydown(event) {
            if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey || editable(event.target))
                return false;
            if (event.code in XR_KEYBOARD_MOVEMENT) {
                event.preventDefault();
                if (event.repeat || pressed.has(event.code)) {
                    record(event.code, 'repeat', event.timeStamp);
                    return true;
                }
                record(event.code, 'down', event.timeStamp);
                pressed.add(event.code);
                cruise = toggleXrKeyboardCruise(cruise, event.code);
                return true;
            }
            const binding = bindings.get(event.code);
            if (!binding)
                return false;
            if (!event.repeat && binding.onKey() === false)
                return false;
            event.preventDefault();
            return true;
        },
        keyup(event) {
            if (pressed.has(event.code))
                record(event.code, 'up', event.timeStamp);
            pressed.delete(event.code);
        },
        /** Stops all keyboard movement, e.g. on focus loss. */
        release() {
            if (cruise.size || pressed.size) {
                stats.releases++;
                if (trace.length < 400)
                    trace.push(['x', '', Math.round(globalThis.performance?.now() ?? NaN)]);
            }
            cruise = new Set();
            pressed.clear();
            lastSeen.clear();
        },
        sticks: () => resolveXrKeyboardSticks(cruise),
        /** Movement keys currently latched on. */
        cruising: () => [...cruise],
        stats: () => stats,
        resetStats() { stats = emptyStats(); },
        /** Movement-key events since the last drain, oldest first. */
        drainTrace() {
            const drained = trace;
            trace = [];
            return drained;
        },
        getSnapshot: () => snapshot,
        subscribe(listener) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
    };
}
export const spaceCanvasXrKeyboard = createXrKeyboard();
